import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, X } from 'lucide-react'
import ProfileLink from '../../../components/ProfileLink'
import { personName } from '../utils/teamFormat'
import { formatSigned, itemSurfaceStyle, profitClass } from '../utils/teamStats'
import { formatAgo } from '../../profile/utils/profileFormat'
import { formatPrice, parseSkinName } from '../../../utils/formatters'

/** Плитка предмета: картинка на подложке цвета редкости, название и цена */
const ItemChip = ({ item }) => {
  const { title, subtitleCompact } = parseSkinName(item?.name || '')

  return (
    <div
      className="flex min-w-0 items-center gap-2 rounded-lg border border-white/5 bg-surface px-2 py-1.5"
      style={itemSurfaceStyle(item)}
    >
      {item?.img && (
        <img src={item.img} alt={item?.name || ''} className="h-8 w-10 shrink-0 object-contain" loading="lazy" />
      )}
      <div className="min-w-0">
        <p className="truncate text-[11px] font-semibold text-white">{title || item?.name}</p>
        <p className="truncate text-[10px] tabular-nums text-text-secondary">
          {subtitleCompact ? `${subtitleCompact} · ` : ''}{formatPrice(item?.crystals ?? 0)}
        </p>
      </div>
    </div>
  )
}

/**
 * Строка одной игры внутри сессии (docs/team-stats-api.md, §5, элемент `items`).
 *
 * У кейсов, апгрейдера и контракта своей страницы игры нет — экран сессии фронт
 * строит сам. Формат строки зависит от режима:
 * - `case` — кто открыл, какой кейс, что выпало (`is_multi` — предметов несколько);
 * - `upgrader` — донор (`bet_items[0]`) → результат, «×target_mult», удался ли;
 * - `contract` — скины на входе → полученный скин.
 *
 * Проигранный апгрейд: `is_win: false` и пустой `win_items` — на какой скин шёл
 * игрок, бэкенд не хранит, есть только множитель, поэтому картинки цели нет.
 */
const TeamSessionItem = ({ item, accent }) => {
  const { t } = useTranslation()
  const name = personName(item, t)
  const betItems = Array.isArray(item.bet_items) ? item.bet_items : []
  const winItems = Array.isArray(item.win_items) ? item.win_items : []
  const isFailedUpgrade = item.kind === 'upgrader' && !item.is_win

  return (
    <div className="rounded-xl border border-white/5 bg-dark-primary p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
      <div className="flex items-center gap-2.5">
        <ProfileLink slug={item.slug} className="shrink-0">
          <span className="block h-7 w-7 overflow-hidden rounded-full border border-white/10 bg-surface">
            {item.ava ? <img src={item.ava} alt={name} className="h-full w-full object-cover" /> : null}
          </span>
        </ProfileLink>

        <div className="min-w-0 flex-1">
          <ProfileLink slug={item.slug} className="block">
            <span className="block truncate text-xs font-semibold text-white">{name}</span>
          </ProfileLink>
          <span className="block text-[10px] text-text-secondary">{formatAgo(item.ago, t)}</span>
        </div>

        {/* Апгрейдер: множитель, на который шёл игрок (у остальных режимов null) */}
        {item.kind !== 'mines' && item.target_mult != null && (
          <span
            className="shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold tabular-nums"
            style={{ borderColor: `${accent}55`, backgroundColor: `${accent}14`, color: accent }}
          >
            ×{item.target_mult}
          </span>
        )}

        <div className="shrink-0 text-right">
          <p className={`text-xs font-bold tabular-nums ${profitClass(item.profit)}`}>
            {formatSigned(item.profit ?? 0)}
          </p>
          <p className="text-[10px] tabular-nums text-text-secondary">
            {t('team.stats.betShort', 'ставка {{bet}}', { bet: formatPrice(item.bet ?? 0) })}
          </p>
        </div>
      </div>

      {item.kind === 'mines' && item.mines && <p className="mt-2 text-xs tabular-nums text-text-secondary">{t('mines.feedSteps', { mines: item.mines.mines, step: item.mines.steps })}{item.target_mult != null && <> · <bdi dir="ltr">×{item.target_mult}</bdi></>}</p>}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {/* Кейс: у апгрейдера и контракта `case: null` */}
        {item.case && (
          <div className="flex min-w-0 items-center gap-2 rounded-lg border border-white/5 bg-surface px-2 py-1.5">
            {item.case.img && (
              <img src={item.case.img} alt={item.case.name} className="h-8 w-10 shrink-0 rounded object-cover" loading="lazy" />
            )}
            <p className="truncate text-[11px] font-semibold text-white">{item.case.name}</p>
          </div>
        )}

        {betItems.map((betItem, index) => (
          <ItemChip key={`bet-${index}`} item={betItem} />
        ))}

        {(item.case || betItems.length > 0) && (
          <ArrowRight size={14} className="shrink-0 text-text-secondary" />
        )}

        {isFailedUpgrade ? (
          <span className="flex items-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/10 px-2.5 py-1.5 text-[11px] font-semibold text-red-400">
            <X size={12} />
            {t('team.stats.upgradeFailed', 'не вышло')}
          </span>
        ) : (
          winItems.map((winItem, index) => (
            <ItemChip key={`win-${index}`} item={winItem} />
          ))
        )}
      </div>
    </div>
  )
}

export default TeamSessionItem
